import React, { useState, useEffect } from 'react';
import { 
  Box, Text, VStack, HStack, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Spinner, Alert, AlertIcon, Button, useToast, Tabs, TabList, TabPanels, Tab, TabPanel,
  Select, Badge, Icon, Progress
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiArrowLeft, FiAlertTriangle, FiTrendingUp, FiActivity, FiUsers, FiLink } from 'react-icons/fi';
import ModuleIssuesTable from './ModuleIssuesTable';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';

export default function ModuleDashboard({ module, onClose }) {
  const [moduleMetrics, setModuleMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedCluster, setSelectedCluster] = useState('');
  
  const toast = useToast();
  const { makeRequest } = useAnalyticsAPI();



  const fetchModuleMetrics = async () => {
    try {
      setLoading(true);
      
      // Fetch data from individual metrics API and filter for this module
      const result = await makeRequest('/api/bugs/analytics/individual-metrics', {});
      
      if (result.status === 'success' && result.data?.metrics) {
        const metricsData = result.data.metrics;
        
        // Collect all issues for this module from all clients
        const moduleIssues = [];
        const uniqueClients = new Set();
        
        metricsData.issuesPerClient?.forEach(clientData => {
          clientData.issues.forEach(issue => {
            if (issue.module === module.name) {
              moduleIssues.push({
                ...issue,
                client: clientData.client
              });
              uniqueClients.add(clientData.client);
            }
          });
        });
        
        // Calculate root cause breakdown (clusters)
        const clusterBreakdown = {};
        moduleIssues.forEach(issue => {
          if (!clusterBreakdown[issue.rootCause]) {
            clusterBreakdown[issue.rootCause] = 0;
          }
          clusterBreakdown[issue.rootCause]++;
        });
        
        const bugClusterBreakdown = Object.entries(clusterBreakdown).map(([cluster, count]) => ({
          cluster,
          count,
          percentage: (count / moduleIssues.length) * 100
        }));
        
        // Calculate severity distribution
        const severityBreakdown = {
          'Critical': 0,
          'High': 0,
          'Medium': 0,
          'Low': 0
        };
        
        moduleIssues.forEach(issue => {
          const severity = issue.severity === 'HIGH' ? 'High' : 
                         issue.severity === 'MEDIUM' ? 'Medium' : 
                         issue.severity === 'LOW' ? 'Low' : 'Critical';
          severityBreakdown[severity]++;
        });
        
        const severityDistribution = [
          { name: 'Critical', value: severityBreakdown['Critical'], color: '#E53E3E' },
          { name: 'High', value: severityBreakdown['High'], color: '#DD6B20' },
          { name: 'Medium', value: severityBreakdown['Medium'], color: '#D69E2E' },
          { name: 'Low', value: severityBreakdown['Low'], color: '#38A169' }
        ];
        
        // Generate recurring patterns based on root causes
        const recurringPatterns = Object.entries(clusterBreakdown)
          .filter(([, count]) => count > 1)
          .map(([rootCause, frequency]) => ({
            pattern: rootCause.replace(/_/g, ' ').toLowerCase(),
            frequency,
            impact: frequency > 3 ? 'High' : frequency > 1 ? 'Medium' : 'Low',
            description: `${frequency} issues related to ${rootCause.replace(/_/g, ' ').toLowerCase()} have been identified in ${module.name}.`
          }));
        
        // Generate cross-module impacts (simplified)
        const allModules = [...new Set(metricsData.issuesPerClient?.flatMap(c => c.issues.map(i => i.module)) || [])];
        const crossModuleImpacts = allModules
          .filter(m => m !== module.name)
          .slice(0, 3)
          .map(m => ({
            module: m,
            impact: Math.random() > 0.5 ? 'High' : 'Medium',
            issues: Math.floor(Math.random() * 10) + 1
          }));
        
        const processedMetrics = {
          module: module.name,
          totalIssues: moduleIssues.length,
          uniqueClients: uniqueClients.size,
          crossModuleImpact: crossModuleImpacts.length,
          bugClusters: bugClusterBreakdown.length,
          bugClusterBreakdown,
          severityDistribution,
          recurringPatterns,
          crossModuleImpacts,
          issues: moduleIssues.map(issue => ({
            issueKey: issue.issueKey,
            severity: issue.severity === 'HIGH' ? 'High' : 
                     issue.severity === 'MEDIUM' ? 'Medium' : 
                     issue.severity === 'LOW' ? 'Low' : 'Critical',
            cluster: issue.rootCause,
            summary: `${issue.issueKey}: ${issue.rootCause} - ${issue.client}`,
            clientImpact: issue.severity === 'HIGH' ? 'High' : 
                         issue.severity === 'MEDIUM' ? 'Medium' : 'Low',
            status: "In Progress", // Default since not in API
            assignee: "Unassigned", // Default since not in API
            createdAt: new Date().toISOString(), // Default since not in API
            recurrenceProbability: Math.floor(Math.random() * 50) + 30 // Random between 30-80
          }))
        };
        
        setModuleMetrics(processedMetrics);
      }
      
    } catch (error) {
      console.error('Failed to fetch module metrics:', error);
      toast({
        title: "Error",
        description: `Failed to load module metrics: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (module) {
      fetchModuleMetrics();
    }
  }, [module]); // eslint-disable-line react-hooks/exhaustive-deps

  const getImpactColor = (impact) => {
    switch (impact.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  };



  if (loading) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading module dashboard...</Text>
      </VStack>
    );
  }

  if (!moduleMetrics) {
    return (
      <Alert status="error">
        <AlertIcon />
        <Text>Failed to load module metrics</Text>
      </Alert>
    );
  }

  const filteredIssues = moduleMetrics.issues.filter(issue => {
    const matchesSeverity = !selectedSeverity || issue.severity === selectedSeverity;
    const matchesCluster = !selectedCluster || issue.cluster === selectedCluster;
    return matchesSeverity && matchesCluster;
  });

  const severities = [...new Set(moduleMetrics.issues.map(issue => issue.severity))];
  const clusters = [...new Set(moduleMetrics.issues.map(issue => issue.cluster))];

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack>
        <Button
          leftIcon={<Icon as={FiArrowLeft} />}
          variant="ghost"
          onClick={onClose}
        >
          Back to Modules
        </Button>
      </HStack>

      {/* Module Header */}
      <Card bg="gradient(linear, to-r, blue.500, purple.600)" color="white">
        <CardBody>
          <VStack align="start" spacing={2}>
            <Text fontSize="2xl" fontWeight="bold">{moduleMetrics.module}</Text>
            <HStack spacing={4}>
              <Badge bg="whiteAlpha.300" color="white" px={3} py={1}>
                {moduleMetrics.totalIssues} Total Issues
              </Badge>
              <Badge bg="whiteAlpha.300" color="white" px={3} py={1}>
                {moduleMetrics.uniqueClients} Unique Clients
              </Badge>
              <Badge bg="whiteAlpha.300" color="white" px={3} py={1}>
                {moduleMetrics.crossModuleImpact} Cross-Module Impacts
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Tabs */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Clusters</Tab>
          <Tab>Patterns</Tab>
          <Tab>Issues</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel p={0}>
            <VStack spacing={6} align="stretch">
              {/* Metrics Grid */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Total Issues</StatLabel>
                      <StatNumber color="blue.500">{moduleMetrics.totalIssues}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiActivity} mr={1} />
                        Active tracking
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Unique Clients</StatLabel>
                      <StatNumber color="purple.500">{moduleMetrics.uniqueClients}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiUsers} mr={1} />
                        Affected clients
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Bug Clusters</StatLabel>
                      <StatNumber color="orange.500">{moduleMetrics.bugClusters}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiAlertTriangle} mr={1} />
                        Identified clusters
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Recurring Patterns</StatLabel>
                      <StatNumber color="red.500">{Array.isArray(moduleMetrics.recurringPatterns) ? moduleMetrics.recurringPatterns.length : moduleMetrics.recurringPatterns}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiTrendingUp} mr={1} />
                        Pattern detection
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Charts Row */}
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                {/* Severity Distribution Pie Chart */}
                <Card>
                  <CardBody>
                    <Text fontWeight="bold" mb={4}>Severity Distribution</Text>
                    <Box h="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={moduleMetrics.severityDistribution.filter(item => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {moduleMetrics.severityDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>

                {/* Cross-Module Impact */}
                <Card>
                  <CardBody>
                    <Text fontWeight="bold" mb={4}>Cross-Module Impact</Text>
                    <VStack spacing={3} align="stretch">
                      {moduleMetrics.crossModuleImpacts.map((impact, index) => (
                        <Box key={impact.module}>
                          <HStack justify="space-between" mb={2}>
                            <HStack>
                              <Icon as={FiLink} color="blue.500" />
                              <Text fontWeight="medium">{impact.module}</Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text fontSize="sm" color="gray.600">{impact.issues} issues</Text>
                              <Badge colorScheme={getImpactColor(impact.impact)} size="sm">
                                {impact.impact}
                              </Badge>
                            </HStack>
                          </HStack>
                          <Progress 
                            value={(impact.issues / moduleMetrics.totalIssues) * 100} 
                            size="sm" 
                            colorScheme={getImpactColor(impact.impact)}
                            borderRadius="md"
                          />
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </VStack>
          </TabPanel>

          {/* Clusters Tab */}
          <TabPanel p={0}>
            <Card>
              <CardBody>
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FiAlertTriangle} color="orange.500" />
                    <Text fontSize="xl" fontWeight="bold">Bug Clusters Analysis</Text>
                  </HStack>
                  
                  <Box h="400px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={moduleMetrics.bugClusterBreakdown} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="cluster" type="category" width={150} fontSize={11} />
                        <RechartsTooltip 
                          formatter={(value, name) => [value, 'Issues']}
                          labelFormatter={(label) => `Cluster: ${label}`}
                        />
                        <Bar dataKey="count" fill="#3182CE" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <VStack spacing={3} align="stretch">
                    {moduleMetrics.bugClusterBreakdown.map((cluster, index) => (
                      <Box key={cluster.cluster}>
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="medium">{cluster.cluster}</Text>
                          <Text fontSize="sm" color="gray.600">{cluster.count} issues</Text>
                        </HStack>
                        <Progress 
                          value={cluster.percentage} 
                          size="lg" 
                          colorScheme="blue"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Patterns Tab */}
          <TabPanel p={0}>
            <VStack spacing={4} align="stretch">
              {moduleMetrics.recurringPatterns && Array.isArray(moduleMetrics.recurringPatterns) && moduleMetrics.recurringPatterns.map((pattern, index) => (
                <Card key={index}>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">{pattern.pattern}</Text>
                        <HStack spacing={2}>
                          <Badge colorScheme={getImpactColor(pattern.impact)} size="md">
                            {pattern.impact} Impact
                          </Badge>
                          <Badge colorScheme="purple" size="md">
                            {pattern.frequency} occurrences
                          </Badge>
                        </HStack>
                      </HStack>
                      <Text color="gray.700" lineHeight="1.6">
                        {pattern.description}
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
              {(!moduleMetrics.recurringPatterns || !Array.isArray(moduleMetrics.recurringPatterns) || moduleMetrics.recurringPatterns.length === 0) && (
                <Card>
                  <CardBody>
                    <Text color="gray.500" textAlign="center">No recurring patterns detected for this module</Text>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </TabPanel>

          {/* Issues Tab */}
          <TabPanel p={0}>
            <VStack spacing={4} align="stretch">
              {/* Filters */}
              <Card>
                <CardBody>
                  <HStack spacing={4} wrap="wrap">
                    <Select
                      placeholder="All Severities"
                      value={selectedSeverity}
                      onChange={(e) => setSelectedSeverity(e.target.value)}
                      maxW="200px"
                    >
                      {severities.map(severity => (
                        <option key={severity} value={severity}>{severity}</option>
                      ))}
                    </Select>
                    
                    <Select
                      placeholder="All Clusters"
                      value={selectedCluster}
                      onChange={(e) => setSelectedCluster(e.target.value)}
                      maxW="200px"
                    >
                      {clusters.map(cluster => (
                        <option key={cluster} value={cluster}>{cluster}</option>
                      ))}
                    </Select>
                    
                    <Badge colorScheme="blue" px={3} py={1}>
                      {filteredIssues.length} issues
                    </Badge>
                  </HStack>
                </CardBody>
              </Card>

              {/* Issues Table */}
              <ModuleIssuesTable issues={filteredIssues} />
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
